import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import Navbar from "../../components/Navbar";
import Sidebar from "../../components/Sidebar";
import "../../styles/Dashboard.css";
import { getData, patchData } from "../../services/Request";
import { useLoader } from "../../context/LoaderContext";
import { toast } from "react-toastify";
import { formatDate } from "../../utils/dateFormat";

const parseItemsField = (items) => {
  if (!items) return [];
  if (Array.isArray(items)) return items;
  if (typeof items === "string") {
    try {
      const parsed = JSON.parse(items);
      return Array.isArray(parsed) ? parsed : [];
    } catch (error) {
      console.error("Failed to parse items", error);
      return [];
    }
  }
  return [];
};

const computeTotals = (items = []) => {
  return items.reduce(
    (acc, item) => {
      const qty = parseFloat(item?.quantity) || 0;
      const price = parseFloat(item?.valueSalesExcludingST) || 0;
      const discount = parseFloat(item?.discount) || 0;
      const gross = qty * price;
      const lineBase = Math.max(gross - discount, 0);
      const tax = parseFloat(item?.salesTaxApplicable) || 0;
      acc.subtotal += gross;
      acc.tax += tax;
      acc.discount += discount;
      acc.total += lineBase + tax;
      return acc;
    },
    { subtotal: 0, tax: 0, discount: 0, total: 0 }
  );
};

const normalizeSale = (payload) => {
  if (!payload) return null;

  let saleData = null;
  if (payload.sale) {
    saleData = payload.sale;
  } else if (payload?.data?.sale) {
    saleData = payload.data.sale;
  } else if (payload?.data && !Array.isArray(payload.data)) {
    saleData = payload.data;
  } else {
    saleData = payload;
  }

  if (!saleData) return null;
  return {
    ...(saleData || {}),
    items: parseItemsField(saleData?.items),
  };
};

export default function QuotationInvoice() {
  const { saleId: saleIdParam } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const locationState = location.state || {};
  const searchParams = new URLSearchParams(location.search);
  const queryInvoice = searchParams.get("invoiceNo");
  const { showLoader, hideLoader } = useLoader();
  const [sale, setSale] = useState(locationState?.sale || null);
  const [printMode, setPrintMode] = useState("a4");

  const items = useMemo(
    () => parseItemsField(sale?.items || locationState?.items),
    [sale?.items, locationState?.items]
  );
  const invoiceNo =
    sale?.invoiceRefNo || queryInvoice || locationState?.invoiceNo || "";
  const saleId = sale?.id || sale?._id || sale?._saleId || saleIdParam || "";
  const displayNumber = invoiceNo || saleId;

  const totals = useMemo(() => computeTotals(items), [items]);

  useEffect(() => {
    const fetchSale = async () => {
      if (!saleIdParam && !saleId) return;
      // If sale is already provided via navigation state, skip fetch
      if (sale) return;
      try {
        showLoader();
        const response = await getData(`api/sales/${saleIdParam || saleId}`);
        setSale(normalizeSale(response));
      } catch (error) {
        toast.error("Invoice details not found.");
        console.error("Failed to load invoice", error);
      } finally {
        hideLoader();
      }
    };
    fetchSale();
  }, [hideLoader, saleId, saleIdParam, showLoader, sale]);

  const setBodyPrintClass = (mode) => {
    const modes = ["print-mode-a4", "print-mode-half", "print-mode-thermal"];
    modes.forEach((cls) => document.body.classList.remove(cls));
    document.body.classList.add(`print-mode-${mode}`);
  };

  const handlePrint = (mode = "a4") => {
    setPrintMode(mode);
    setBodyPrintClass(mode);
    window.print();
    setTimeout(() => {
      setBodyPrintClass("a4");
    }, 300);
  };

  const handleGenerateInvoice = async () => {
    if (!saleId) {
      toast.error("Sale ID missing.");
      return;
    }

    // If already an invoice, just open the invoice view.
    if (sale?.invoice) {
      navigate(`/invoice/${saleId}`, { state: { sale } });
      return;
    }

    const toastId = toast.loading("Generating invoice...");
    try {
      showLoader();

      const response = await patchData(`api/sales/${saleId}/invoice`);
      const refreshedSale = await getData(`api/sales/${saleId}`);
      const updatedSale = normalizeSale(refreshedSale) || sale;
      setSale(updatedSale);

      toast.update(toastId, {
        render: response?.message || "Invoice generated.",
        type: "success",
        isLoading: false,
        autoClose: 1500,
      });
      navigate(`/invoice/${saleId}`, { state: { sale: updatedSale } });
    } catch (error) {
      toast.update(toastId, {
        render: error?.message || "Invoice could not be generated.",
        type: "error",
        isLoading: false,
        autoClose: 2000,
      });
      console.error("Failed to generate invoice", error);
    } finally {
      hideLoader();
    }
  };

  return (
    <div className="app-shell">
      <Sidebar />
      <div className="app-content">
        <div className="dashboard-page">
          <Navbar />
          <main className="dashboard">
            <section className="invoice-page">
              <header className="invoice-page__header">
                <div className="invoice-page__brand">
                  <div>
                    <h1>Quotation #{displayNumber || saleId}</h1>
                    <p className="muted-text">
                      Date: {formatDate(sale?.invoiceDate)}
                    </p>
                    <p className="muted-text">
                      Due: {formatDate(sale?.dueDate)}
                    </p>
                  </div>
                </div>
                <div className="invoice-page__actions">
                  <button
                    type="button"
                    className="primary-btn"
                    onClick={handleGenerateInvoice}
                  >
                    Generate Invoice
                  </button>
                  <div className="print-options">
                    <button
                      type="button"
                      className={`outline-btn ${
                        printMode === "a4" ? "is-active" : ""
                      }`}
                      onClick={() => handlePrint("a4")}
                    >
                      Print Quotation
                    </button>
                    {/* <button
                      type="button"
                      className={`outline-btn ${
                        printMode === "thermal" ? "is-active" : ""
                      }`}
                      onClick={() => handlePrint("thermal")}
                    >
                      Thermal print
                    </button> */}
                  </div>
                  <button
                    type="button"
                    className="outline-btn"
                    onClick={() => navigate(-1)}
                  >
                    Back
                  </button>
                </div>
              </header>

              <div className="invoice-page__parties">
                <div>
                  <p className="eyebrow">Seller</p>
                  <h3>{sale?.sellerBusinessName || "-"}</h3>
                  <p className="muted-text">{sale?.sellerNTNCNIC || "-"}</p>
                  <p className="muted-text">{sale?.sellerProvince || "-"}</p>
                  <p className="muted-text">{sale?.sellerAddress || "-"}</p>
                  <p className="muted-text">
                    Sales person:{" "}
                    {sale?.salesPersonName || sale?.salePerson || "-"}
                  </p>
                </div>
                <div>
                  <p className="eyebrow">Buyer</p>
                  <h3>{sale?.buyerBusinessName || "-"}</h3>
                  <p className="muted-text">{sale?.buyerNTNCNIC || "-"}</p>
                  <p className="muted-text">{sale?.buyerProvince || "-"}</p>
                  <p className="muted-text">{sale?.buyerAddress || "-"}</p>
                </div>
              </div>

              <div className="invoice-page__table-wrapper">
                <table className="invoice-page__table">
                  <thead>
                    <tr>
                      <th>Product</th>
                      <th>Description</th>
                      <th>Qty</th>
                      <th>Price</th>
                      <th>Discount</th>
                      <th>Tax %</th>
                      <th>Tax</th>
                      <th>Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.length === 0 && (
                      <tr>
                        <td colSpan="8" className="empty-state">
                          No items on this invoice.
                        </td>
                      </tr>
                    )}
                    {items.map((item, index) => {
                      const qty = parseFloat(item?.quantity) || 0;
                      const price =
                        parseFloat(item?.valueSalesExcludingST) || 0;
                      const discount = parseFloat(item?.discount) || 0;
                      const gross = qty * price;
                      const net = Math.max(gross - discount, 0);
                      const taxPercent = parseFloat(item?.rate) || 0;
                      const tax = parseFloat(item?.salesTaxApplicable) || 0;
                      const total = parseFloat(item?.totalValues) || net + tax;
                      return (
                        <tr key={`${item?.hsCode || "item"}-${index}`}>
                          <td>{item?.productName || item?.hsCode || "-"}</td>
                          <td>{item?.productDescription || "-"}</td>
                          <td>{qty}</td>
                          <td>{price}</td>
                          <td>{discount}</td>
                          <td>{taxPercent}%</td>
                          <td>{tax.toFixed(2)}</td>
                          <td>{total.toFixed(2)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div className="invoice-page__totals">
                <div>
                  <span>Subtotal</span>
                  <strong>{totals.subtotal.toFixed(2)}</strong>
                </div>
                <div>
                  <span>Discount</span>
                  <strong>-{totals.discount.toFixed(2)}</strong>
                </div>
                <div>
                  <span>Tax</span>
                  <strong>{totals.tax.toFixed(2)}</strong>
                </div>

                <div className="invoice-page__grand">
                  <span>Total</span>
                  <strong>{totals.total.toFixed(2)}</strong>
                </div>
              </div>
            </section>
          </main>
        </div>
      </div>
    </div>
  );
}
